/**
 * Account-level Vendor Directory: every project's vendors in one list.
 *
 * GET /planning/account/vendors[?include_archived=true] answers
 * { vendors: [{ id, name, category, categories, status, instagram, ..., project_id, project_name, rating? }] }.
 * The rows are the project vendors list plus the project each belongs to, so
 * both spellings of a field the project list uses (vendor_id / id, review_count
 * / reviewCount, a bare rating number or a { average, count } summary) are read.
 * Ids are uuids: they are passed through apiId, never Number().
 */
import { apiGet } from './PlanningApiClient';
import { apiId } from '../utils/apiId';

export interface AccountVendor {
  /** The vendor's own id, used in its page URL. */
  id: string;
  name: string;
  category: string | null;
  categories: string[];
  status: string | null;
  archived: boolean;
  instagram: string | null;
  /** Average rating, or null when nobody has rated the vendor. */
  rating: number | null;
  ratingCount: number;
  projectId: string | null;
  projectName: string;
}

type Raw = Record<string, unknown>;

const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);
const num = (v: unknown): number | null => {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};

function readRating(row: Raw): { rating: number | null; count: number } {
  const nested = (row.rating_summary ?? row.ratingSummary ?? (typeof row.rating === 'object' ? row.rating : null)) as Raw | null;
  const average = nested ? num(nested.average ?? nested.avg ?? nested.rating) : num(row.rating ?? row.average_rating);
  const count = (nested ? num(nested.count ?? nested.review_count) : null) ?? num(row.review_count ?? row.reviewCount ?? row.rating_count) ?? 0;
  // A zero average is "not rated yet" in the project list, not a one-star-below-minimum score.
  return { rating: average !== null && average > 0 ? average : null, count };
}

export function fromApiVendor(row: Raw): AccountVendor | null {
  const id = apiId((row.id ?? row.vendor_id) as string | number | null | undefined);
  const name = text(row.name);
  if (!id || !name) return null;
  const categories = Array.isArray(row.categories)
    ? row.categories.map(c => (typeof c === 'string' ? c : text((c as Raw | null)?.name))).filter((c): c is string => !!c)
    : [];
  const status = text(row.status);
  const { rating, count } = readRating(row);
  return {
    id,
    name,
    category: text(row.category) ?? categories[0] ?? null,
    categories,
    status,
    archived: row.archived === true || status?.toLowerCase() === 'archived',
    instagram: text(row.instagram),
    rating,
    ratingCount: count,
    projectId: apiId((row.project_id ?? row.wedding_id) as string | number | null | undefined),
    projectName: text(row.project_name) ?? 'Unnamed project',
  };
}

/** Active vendors of every project, or (with includeArchived) the archived ones too. */
export async function loadAccountVendors(includeArchived: boolean): Promise<AccountVendor[]> {
  const query = includeArchived ? '?include_archived=true' : '';
  const body = await apiGet<{ vendors?: Raw[]; data?: Raw[] } | Raw[]>(`/planning/account/vendors${query}`);
  const rows = Array.isArray(body) ? body : (body?.vendors ?? body?.data ?? []);
  return rows.map(fromApiVendor).filter((v): v is AccountVendor => v !== null);
}
